import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import { useEffect, useState } from 'react';
import CreateCancel from "../../atoms/buttons/CreateCancel";
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import WarningIcon from '@material-ui/icons/Warning';
import React from 'react';
import { DialogTitle, Icon, IconButton, Tooltip, Typography } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import Pagination from '@material-ui/lab/Pagination';
import ContactSupportIcon from '@material-ui/icons/ContactSupport';
import { getDetailsFromUrl } from '../../../services/blueprint';
import { triggerTour } from '../../../store/global/actions';
import { useDispatch } from 'react-redux';
import SkeletonLoader from '../../atoms/loader/SkeletonLoader';
import useConstructsNavigation from '../../../hooks/useConstructsNavigation';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        fontSize: "1.5rem", // H2 size
    },
    actionsRight: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    paginationContainer: {
        width: "100%",
        display: "flex",
        justifyContent: "center",
        padding: 0,
        paddingTop: '10px'
    },
    listItem: {
        cursor: "pointer",
        "&:hover $listItemText": {
            textDecoration: "underline",
        },
    },
    listItemText: {
        transition: "text-decoration 0.2s ease",
    },
    dialogActions: {
        display: "flex",
        flexDirection: 'column'
    },
    actionButtons: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end'
    }
}));

function BlueprintValidationsList({ loading, setLoading, handleRefresh, open, onClose, validations, onDeployment = false, deploymentData }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { navigateToConstruct } = useConstructsNavigation();
    const { screen, entityId } = getDetailsFromUrl();

    const [filteredValidations, setSortedValidations] = useState([validations] || []);
    const [page, setPage] = useState(1);

    const rowsPerPage = 10;
    const totalPages = Math.ceil(filteredValidations.length / rowsPerPage);

    const continueDeployment = () => {
        onClose();
        if (onDeployment) onDeployment(deploymentData);
    };

    const onRefreshClick = () => {
        handleRefresh();
        setPage(1); // reset to first page on refresh
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    // Slice validations based on page
    const paginatedValidations = filteredValidations.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const onItemClick = (item) => {
        
        onClose();
        if (item?.mainEntityType === "view") {
            localStorage.setItem("VIEW_TAB_ONFOCUS", "datasource");
            navigateToConstruct({
                mainEntityId: item?.mainEntityId,
                mainEntityType: item?.mainEntityType,
            });

        }else if(item?.mainEntityType === "testsuite"){
            const testsuite = item?.entityItem;
            const testcase = testsuite?.testcase;
            
            const isContextValidation = testsuite?.context_validation || false;
            const isTestcaseContext = testsuite?.testcase_context;
            

            const isEventValidation = testsuite?.event_validation || false;
            const focusEvent = isEventValidation && testsuite.event ? testsuite.event  : null;

            navigateToConstruct({
                mainEntityId: item?.mainEntityId,
                mainEntityType: item?.mainEntityType,
                subEntity: testcase,
                entityId: testcase._id.toString(),
                entityType: isEventValidation ? "eventPayload" : isTestcaseContext ? "testcase" : "testsuite",
                entityItem: isEventValidation ? focusEvent : isTestcaseContext ? testcase : testsuite,
                tabOnfocus: item?.tabOnfocus,
            });

        }else {
            navigateToConstruct({
                mainEntityId: item?.mainEntityId,
                mainEntityType: item?.mainEntityType,
                entityId: item?.entityId,
                entityType: item?.entityType,
                entityItem: item?.entityItem,
                tabOnfocus: item?.tabOnfocus || 0
            });
        }
    }



    const sortValidations = (validations) => {
        try {
            if (screen === "transaction" || screen === "view") {
                const sortedItems = [...validations].sort((a, b) => {
                    const aIsMatch =
                        a.mainEntityType === screen && a.mainEntityId === entityId;
                    const bIsMatch =
                        b.mainEntityType === screen && b.mainEntityId === entityId;

                    if (aIsMatch && !bIsMatch) return -1; // a comes first
                    if (!aIsMatch && bIsMatch) return 1;  // b comes first
                    return 0; // keep original order otherwise
                });

                setSortedValidations(sortedItems);
                return sortedItems;
            } else if (screen === "platform-schema") {
                const sortedItems = [...validations].sort((a, b) => {
                    const aIsMatch = a.entityType === "routing_rule" && a.severity === "error";
                    const bIsMatch = b.entityType === 'routing_rule' && a.severity === "error";

                    if (aIsMatch && !bIsMatch) return -1; // a comes first
                    if (!aIsMatch && bIsMatch) return 1;  // b comes first
                    return 0; // keep original order otherwise
                });

                setSortedValidations(sortedItems);
                return sortedItems;
            }

            return validations;
        } catch (error) {
            console.error(`Filtering validations failed. ${error}`);
            return validations;
        }
    };

    useEffect(() => {
        handleRefresh();
    }, [])

    useEffect(() => {
        if (validations) {
            setSortedValidations(sortValidations(validations));
        }
    }, [validations])

    const HighlightedText = ({ text, highlights }) => {
        const regex = new RegExp(`(${highlights.join("|")})`, "gi");
        const parts = text.split(regex);

        return (
            <>
                {parts.map((part, i) =>
                    highlights.some(h => h.toLowerCase() === part.toLowerCase()) ? (
                        <strong key={i}>{part}</strong>
                    ) : (
                        part
                    )
                )}
            </>
        );
    }

    const handleManualTour = () => {
        dispatch(triggerTour({ currentTour: 'blueprintValidator', initStart: true }))
    }


    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="customized-dialog-title"
            PaperProps={{ style: { width: 900, maxWidth: 1200 } }}
            id="blueprint_validations_modal"
        >
            <DialogTitle id="validation-title" disableTypography className={classes.dialogTitle}>
                <Typography variant="h2" className={classes.titleText}>
                    Blueprint Validations
                </Typography>

                <div className={classes.actionsRight}>
                    <Tooltip title="Refresh validations">
                        <IconButton id={"refresh_button"} onClick={onRefreshClick} aria-label="refresh" data-ui-tour-id='blueprint-validation-refresh'>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Show Tour'>
                        <Icon onClick={handleManualTour} style={{ height: '2rem', width: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                            <ContactSupportIcon style={{ fontSize: '2rem', color: 'black' }} />
                        </Icon>
                    </Tooltip>

                    <Tooltip title="Close dialog">
                        <IconButton onClick={onClose} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            </DialogTitle>

            <DialogContent style={{ overflow: "hidden" }} id="blueprint-validation-content" dividers>
                {loading &&
                    <SkeletonLoader
                        type="list"
                        listItems={10}
                    />
                }

                {!loading && filteredValidations.length > 0 &&
                    <Grid container style={{ minHeight: "50vh", maxHeight: "70vh", overflowY: "auto", padding: '0 12px' }}>
                        <List id="blueprint-validation-list" component="nav" aria-label="validation-list" style={{ width: "100%" }}>
                            {paginatedValidations.map((item, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        className={`${classes.listItem} validation-list-item`}
                                        button
                                        onClick={() => onItemClick(item)}
                                        data-ui-tour-id='blueprint-validation-message'
                                    >
                                        <ListItemIcon>
                                            {item.severity === "error" ? <ErrorIcon style={{ color: "#db5757" }} /> :
                                                <WarningIcon style={{ color: "#fbc02d" }} />
                                            }
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<HighlightedText text={item.message} highlights={item.highlights || []} />}
                                            className={classes.listItemText}
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))}
                        </List>
                    </Grid>
                }

                {!loading && filteredValidations.length === 0 &&
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="center"
                        style={{ justifyContent: "center" }}
                    >
                        <Typography variant="body1" color="textSecondary">
                            No errors or warnings available.
                        </Typography>
                    </Grid>
                }


            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                {!loading && (
                    <div id={"validator-pagination"} className={classes.paginationContainer}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                        />
                    </div>
                )}

                {onDeployment &&
                    <div className={classes.actionButtons}>
                        <CreateCancel
                            createDisable={loading}
                            content={'Proceed & Deploy'}
                            onCreate={continueDeployment}
                            onCancel={onClose}
                        />
                    </div>}
            </DialogActions>

        </Dialog>
    );
}

export default BlueprintValidationsList;
